import { useCallback } from 'react'

import { ChainOfferProduct, ProductContent } from '@common'
import cx from 'classnames'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'

import BrawlLock from '@/assets/images/brawlstars/brawl_lock.png'
import { DisplayAsset } from '@/features/storefronts/_storeBase/assetRendering/DisplayAsset'
import { Flare } from '@/features/storefronts/_storeBase/flare/Flare'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import {
  useAssetColor,
  AssetColor,
  brawlHyperChargeAsset,
  isBrawlerOrSkin,
} from '@/features/storefronts/brawlstars/bundle/helpers'
import { useTranslatable } from '@/localization/useTranslatable'

import { isBrawler } from '../../../../../../../productFilters'
import { HyperchargeAsset } from '../../../../../BrawlCardProduct'

import styles from './BrawlItem.module.scss'
import colors from '../colors.module.scss'

type Props = {
  content: ProductContent
  isSingleItemOffer?: boolean
  isSubItem?: boolean // when item is shown inside another card item
}

type BrawlChainItemProps = {
  product: ChainOfferProduct
  assetColorOverride: AssetColor
  heading?: string
  showLock?: boolean
}

export const ProductImage = ({ content, isSubItem }: Props) => {
  const translatable = useTranslatable()
  const title = translatable(content.product.title)

  const isBrawlHyperChargeAsset = brawlHyperChargeAsset(content.product.id)

  if (isBrawlHyperChargeAsset) {
    return (
      <div className={styles.ProductImage}>
        <HyperchargeAsset title={title} assetId={content.product.id} />
      </div>
    )
  }

  if (!isSubItem && content.product.assets.animated) {
    return (
      <div className={cx(styles.productImg, styles.AnimatedProductImage)}>
        <DisplayAsset content={content} preferAnimated gameId="brawlstars" />
      </div>
    )
  }

  return (
    <div className={styles.ProductImage}>
      <DisplayAsset content={content} preferAnimated gameId="brawlstars" />
    </div>
  )
}

const BrawlChainItemContentItem = ({ content }: { content: ProductContent }) => {
  const translatable = useTranslatable()
  const t = useTranslations()
  const locale = useLocale()
  const isBrawlerItem = isBrawler(content)

  const getTitle = useCallback(() => {
    const { itemType } = content.product
    if (!itemType) {
      return null
    }

    if (isBrawlerItem) {
      return translatable(content.product.title)
    }

    if (content.product.itemType === 'Skin') {
      return t('skin')
    }

    if (['Coins', 'PowerPoints', 'Gems', 'Bling'].includes(itemType)) {
      return content.amount.toString()
    }

    return null
  }, [content, t, translatable, isBrawlerItem])

  const itemTitle = getTitle()

  return (
    <div className={cx(styles.GridItem, { [styles.Brawler]: isBrawlerItem })}>
      <DisplayAsset gameId="brawlstars" content={content} />
      {itemTitle ? (
        <div className={styles.TitleContainer}>
          <StoreTypography tag="p" style="outlined" textSize="sm" className={cx(styles.Title, styles[locale])}>
            {itemTitle}
          </StoreTypography>
        </div>
      ) : null}
    </div>
  )
}

function chainItemSort(a: ProductContent, b: ProductContent) {
  if (isBrawler(a) || isBrawler(b)) {
    return -1
  }
  return 0
}

export function BrawlChainItem({ product, assetColorOverride, heading, showLock }: BrawlChainItemProps) {
  const gridStyle = (count: number) => {
    switch (count) {
      case 1:
        return styles.one
      case 2:
        return styles.two
      default:
        return styles.three
    }
  }

  return (
    <div className={cx(styles.Card, colors[assetColorOverride], styles.BrawlChainItem)}>
      <Flare game="brawlstars" />
      {showLock ? <Image className={styles.BrawlLock} src={BrawlLock} width={28} height={36} alt="" /> : null}
      {heading ? ( // Might be used later for Day 1, Day 2, etc.
        <StoreTypography tag="p" style="outlined" textSize="body" className={styles.Heading}>
          {heading}
        </StoreTypography>
      ) : null}
      <div className={cx(styles.ChainContentGrid, gridStyle(product.contents.length))}>
        {[...product.contents].sort(chainItemSort).map((content, index) => (
          <BrawlChainItemContentItem key={index} content={content} />
        ))}
      </div>
    </div>
  )
}

export function BrawlItem({ content, isSingleItemOffer, isSubItem }: Props) {
  let colorStyle = ''
  const colorScheme = useAssetColor(content.product.id)
  const translatable = useTranslatable()
  const isBrawlHyperChargeAsset = brawlHyperChargeAsset(content.product.id)
  const isSkinOrBrawler = isBrawlerOrSkin(content)

  let shownTitle = translatable(content.product.title)
  if (!isBrawlHyperChargeAsset && !isSkinOrBrawler) {
    shownTitle = content.amount.toString()
  }
  const cardStyle = isSingleItemOffer ? styles.SingleItemCard : styles.Card

  // for singleItemOffer colorScheme comes from container
  if (!isSingleItemOffer) {
    colorStyle = colors[colorScheme]
  }

  return (
    <div className={cx(cardStyle, colorStyle)}>
      {(isSubItem || (!isSingleItemOffer && !isSkinOrBrawler)) && <Flare />}
      <ProductImage content={content} isSubItem={isSubItem} />
      {!isSingleItemOffer && (
        <div className={styles.FooterWrapper}>
          <div className={styles.Footer}>
            <StoreTypography tag="h3" style="outlined" textSize="sm" className={styles.Title}>
              {shownTitle}
            </StoreTypography>
          </div>
        </div>
      )}
    </div>
  )
}
